/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import {
  AuthClient, IsocialCallUrls, logout, User,
  wsLogin, wsRefresh
} from '@biz-nest/auth-client';
import { Buyer, CommerceClient, Company, Staff } from '@biz-nest/commerce-client';
import { FrontendLogger, IehttpControllerConfig } from '@biz-nest/core-client';
import { NotificationClient } from '@biz-nest/notification-client';
import {
  ChatService,
  CurrencyService, DataService, NotificationsService,
  WindowService
} from '@front-shared-lib';
import {
  AutoCloudDnsClient,
  DeliveryManager, EagleInfoClientError,
  FlyingMailClient,
  IserverAddresses, ManagerClient, VendorManager
} from '@main-shared-lib';
import { AppEnvironmentService } from './app-environment.service';
import { WebStorageService } from './web-storage.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  isLoadingExpress = false;
  isLoggedIn?: boolean;
  confirmEnabled?: boolean;
  webMetricsServer?: string;
  mailMetricsServer?: string;
  notifServer?: string;
  refreshToken?: string;

  constructor(
    private storage: WebStorageService,
    private dataService: DataService,
    private currencyService: CurrencyService,
    private chat: ChatService,
    private notifService: NotificationsService,
    private windowService:WindowService
  ) { }

  storeNewTokensAfterRefresh(accessToken: string, refreshToken: string) {
    return new Promise<void>((resolve) => {
      FrontendLogger.info('Storing new tokens after refresh', accessToken, refreshToken);
      if (accessToken) {
        this.storage.set('access-token', accessToken);
      }
      if (refreshToken) {
        this.storage.set('refresh-token', refreshToken);
      }
      resolve();
    });
  }

  getRefreshTokenForRefresh() {
    return new Promise<string>((resolve) => {
      FrontendLogger.info('Getting refresh token for refresh');
      const refreshToken = this.storage.get('refresh-token');

      FrontendLogger.info('Refresh token', refreshToken);

      resolve(refreshToken || '');
    });
  }

  appendToken(token: string) {
    if (!token) {
      return;
    }
    AuthClient.ehttp.appendToken(token);
    ManagerClient.ehttp.appendToken(token);
    NotificationClient.ehttp.appendToken(token);
    CommerceClient.ehttp.appendToken(token);
    FlyingMailClient.ehttp.appendToken(token);
    AutoCloudDnsClient.ehttp.appendToken(token);
  }

  getWsToken(): Promise<string | undefined> {
    FrontendLogger.info('Getting WS token');
    if (!this.refreshToken) {
      return this.wsLogin();
    }

    return this.wsRefresh();
  }


  async wsLogin() {
    FrontendLogger.info('Logging in');
    const res = await wsLogin();

    FrontendLogger.debug('Logged in', res);

    this.refreshToken = res.ws_refresh_token;

    return res.ws_access_token;
  }

  async wsRefresh() {
    FrontendLogger.info('Refreshing');
    // append to headers
    if (!this.refreshToken) {
      FrontendLogger.error('Refresh token not found');

      return;
    }

    AuthClient.ehttp.appendToHeaders({ ws_refresh_token: this.refreshToken });
    const res = await wsRefresh();

    FrontendLogger.debug('Refreshed', res);

    this.refreshToken = res.ws_refresh_token;

    return res.ws_access_token;
  }

  remeberMe(remeber = true) {
    if (remeber) {
      this.storage.set('remember-me', 'true');
    } else {
      this.storage.set('remember-me', 'false');
    }
  }

  getRememberMe() {
    return this.storage.get('remember-me');
  }

  async logOut() {
    this.isLoggedIn = false;
    await logout();
    this.clearProfile();
    this.isLoggedIn = false;
    // clear store
    this.storage.remove('access-token');
    this.storage.remove('refresh-token');
    window.location.reload();
  }

  clearProfile() {
    this.dataService.staffProfile = undefined;
    this.dataService.vendorManagerProfile = undefined;
    this.dataService.deliveryManagerProfile = undefined;
    this.dataService.companyProfile = undefined;
    this.dataService.profileType = '' as 'buyer' | 'staff' | 'vendorManager' | 'deliveryManager' | 'company';
    this.dataService.companyId = '';
  }

  initAllClients(
    serverAddresses: IserverAddresses,
    production: boolean,
    companyId: string,
    countryCode?: string
  ) {
    FrontendLogger.debug('initing all clients');
    AppEnvironmentService.environment = {
      production
    };

    this.dataService.setChatServerUrl(serverAddresses.chatServer);
    this.webMetricsServer = serverAddresses.webMetricsServer;
    this.mailMetricsServer = serverAddresses.mailMetricsServer;
    this.notifServer = serverAddresses.notifServer;

    this.runAuthClient(serverAddresses.authServer, serverAddresses.authServer, companyId);
    this.runManagerClient(serverAddresses.authServer, serverAddresses.managerServer, companyId);
    this.runNotifClient(serverAddresses.authServer, serverAddresses.notifServer, companyId);
    this.runCommerceClient(serverAddresses.authServer, serverAddresses.commerceServer, companyId);
    this.runMailClient(serverAddresses.authServer, serverAddresses.mailServer, companyId);
    this.runAutoDnsClient(serverAddresses.authServer, serverAddresses.dnsServer, companyId);

    if (countryCode) {
      this.appendHeadersToClients({ 'x-country': countryCode, 'x-country-code': countryCode });
    }

    const accessToken = this.storage.get('access-token');

    FrontendLogger.debug('Access token', accessToken);

    if (accessToken) {
      this.appendToken(accessToken);
    }
  }

  prepareUser(user: User) {
    if (!user) {
      throw new EagleInfoClientError('User not found');
    }

    if (typeof user !== 'object') {
      throw new EagleInfoClientError('User not found');
    }

    this.dataService.authUser = user;
    if (!this.notifServer) {
      throw new EagleInfoClientError('Notif server not found');
    }
    this.notifService.connect(this.notifServer);
    this.isLoggedIn = true;
  }

  prepareBuyer(buyer: Buyer) {
    if (!buyer) {
      throw new EagleInfoClientError('User not found');
    }

    if (typeof buyer !== 'object') {
      throw new EagleInfoClientError('User not found');
    }

    this.dataService.buyerProfile = buyer;
    this.isLoggedIn = true;
    this.dataService.profileType = 'buyer';

    this.currencyService.setCurrency(buyer.defaultCurrency);
  }

  prepareStaff(staff: Staff, company: Company) {
    if (!staff) {
      throw new EagleInfoClientError('User not found');
    }

    if (typeof staff !== 'object') {
      throw new EagleInfoClientError('User not found');
    }

    this.dataService.staffProfile = staff;
    this.isLoggedIn = true;
    this.dataService.profileType = 'staff';
    this.dataService.currentCompany = company;

    if (!staff.commerceUser.company) {
      throw new EagleInfoClientError('User not found');
    }

    this.dataService.companyId = typeof staff.commerceUser.company === 'string' ?
      staff.commerceUser.company : staff.commerceUser.company._id;


    this.currencyService.setCurrency(staff.commerceUser.defaultCurrency);

    this.initCommon();
  }


  prepareVendorManager(vendorManager: VendorManager) {
    if (!vendorManager) {
      throw new EagleInfoClientError('User not found');
    }

    if (typeof vendorManager !== 'object') {
      throw new EagleInfoClientError('User not found');
    }

    this.dataService.vendorManagerProfile = vendorManager;
    this.isLoggedIn = true;
    this.dataService.profileType = 'vendorManager';


    this.currencyService.setCurrency(vendorManager.manager.defaultCurrency);
  }

  prepareDeliveryManager(deliveryManager: DeliveryManager) {
    if (!deliveryManager) {
      throw new EagleInfoClientError('User not found');
    }

    if (typeof deliveryManager !== 'object') {
      throw new EagleInfoClientError('User not found');
    }

    this.dataService.deliveryManagerProfile = deliveryManager;
    this.isLoggedIn = true;
    this.dataService.profileType = 'deliveryManager';


    this.currencyService.setCurrency(deliveryManager.manager.defaultCurrency);
  }

  prepareCompany(company: Company) {
    if (!company) {
      throw new EagleInfoClientError('Company not found');
    }

    if (typeof company !== 'object') {
      throw new EagleInfoClientError('Company not found');
    }

    this.dataService.companyProfile = company;
    this.isLoggedIn = true;
    this.dataService.profileType = 'company';

    if (typeof company.owner !== 'object') {
      throw new EagleInfoClientError('Company not found');
    }

    this.dataService.companyId = company._id;

    this.currencyService.setCurrency(company.owner.defaultCurrency);
    this.dataService.currentCompany = company;

    this.initCommon();
  }

  initCommon() {
    this.appendHeadersToClients({ 'x-company': this.dataService.companyId || '' });
    if (!this.dataService.currentCompany) {
      this.dataService.getCurrentCompany();
    }
    this.chat.runChatController(this.getWsToken.bind(this));
  }

  runAuthClient(authServiceBaseUrl: string, baseURL: string, companyId: string) {
    FrontendLogger.debug('initing auth client');

    const axiosConfig: IehttpControllerConfig = {
      authServiceBaseUrl,
      getRefreshTokenForRefresh: this.getRefreshTokenForRefresh.bind(this),
      storeNewTokensAfterRefresh: this.storeNewTokensAfterRefresh.bind(this),
      dontUserCookie: true,
      baseURL,
      timeout: 2000000,
      headers: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        Authorization: 'auth-token'
      }
    };

    const query = new URLSearchParams({
      fullCallBackUrl: `${this.windowService.getLocation().origin}/auth/callback`
    }).toString();

    const googleLoginUrl = `${authServiceBaseUrl}/auth/login/google?${query}`;
    const facebookLoginUrl = `${authServiceBaseUrl}/auth/login/facebook?${query}`;

    const socialCallUrls: IsocialCallUrls = {
      google: googleLoginUrl,
      facebook: facebookLoginUrl
    };

    new AuthClient(axiosConfig, socialCallUrls);
    AuthClient.ehttp.appendToHeaders({
    // eslint-disable-next-line @typescript-eslint/naming-convention
      'x-company': companyId,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'x-country-code': 'UG'
    });
  }

  runNotifClient(authServiceBaseUrl: string, baseURL: string, companyId: string) {
    FrontendLogger.debug('initing notif client');
    const axiosConfig: IehttpControllerConfig = {
      authServiceBaseUrl,
      getRefreshTokenForRefresh: this.getRefreshTokenForRefresh.bind(this),
      storeNewTokensAfterRefresh: this.storeNewTokensAfterRefresh.bind(this),
      dontUserCookie: true,
      baseURL,
      timeout: 2000000,
      headers: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        Authorization: 'auth-token'
      }
    };

    new NotificationClient(axiosConfig);
    NotificationClient.ehttp.appendToHeaders({
    // eslint-disable-next-line @typescript-eslint/naming-convention
      'x-company': companyId,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'x-country-code': 'UG'
    });
  }

  runCommerceClient(authServiceBaseUrl: string, baseURL: string, companyId: string) {
    FrontendLogger.debug('initing core client');

    const axiosConfig: IehttpControllerConfig = {
      authServiceBaseUrl,
      getRefreshTokenForRefresh: this.getRefreshTokenForRefresh.bind(this),
      storeNewTokensAfterRefresh: this.storeNewTokensAfterRefresh.bind(this),
      dontUserCookie: true,
      baseURL,
      timeout: 2000000,
      headers: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        Authorization: 'auth-token'
      }
    };

    new CommerceClient(axiosConfig);
    CommerceClient.ehttp.appendToHeaders({
    // eslint-disable-next-line @typescript-eslint/naming-convention
      'x-company': companyId,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'x-country-code': 'UG'
    });
  }

  runManagerClient(authServiceBaseUrl: string, baseURL: string, companyId: string) {
    const axiosConfig: IehttpControllerConfig = {
      authServiceBaseUrl,
      getRefreshTokenForRefresh: this.getRefreshTokenForRefresh.bind(this),
      storeNewTokensAfterRefresh: this.storeNewTokensAfterRefresh.bind(this),
      dontUserCookie: true,
      baseURL,
      timeout: 2000000,
      headers: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        Authorization: 'auth-token'
      }
    };

    new ManagerClient(axiosConfig);
    ManagerClient.ehttp.appendToHeaders({
    // eslint-disable-next-line @typescript-eslint/naming-convention
      'x-company': companyId,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'x-country-code': 'UG'
    });
  }

  runMailClient(authServiceBaseUrl: string, baseURL: string, companyId: string) {
    const axiosConfig: IehttpControllerConfig = {
      authServiceBaseUrl,
      getRefreshTokenForRefresh: this.getRefreshTokenForRefresh.bind(this),
      storeNewTokensAfterRefresh: this.storeNewTokensAfterRefresh.bind(this),
      dontUserCookie: true,
      baseURL,
      timeout: 2000000,
      headers: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        Authorization: 'auth-token'
      }
    };

    new FlyingMailClient(axiosConfig);
    FlyingMailClient.ehttp.appendToHeaders({
    // eslint-disable-next-line @typescript-eslint/naming-convention
      'x-company': companyId,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'x-country-code': 'UG'
    });
  }


  runAutoDnsClient(authServiceBaseUrl: string, baseURL: string, companyId: string) {
    const axiosConfig: IehttpControllerConfig = {
      authServiceBaseUrl,
      getRefreshTokenForRefresh: this.getRefreshTokenForRefresh.bind(this),
      storeNewTokensAfterRefresh: this.storeNewTokensAfterRefresh.bind(this),
      dontUserCookie: true,
      baseURL,
      timeout: 2000000,
      headers: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        Authorization: 'auth-token'
      }
    };

    new AutoCloudDnsClient(axiosConfig);
    AutoCloudDnsClient.ehttp.appendToHeaders({
    // eslint-disable-next-line @typescript-eslint/naming-convention
      'x-company': companyId,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'x-country-code': 'UG'
    });
  }

  appendHeadersToClients(headers: { [key: string]: string }) {
    AuthClient.ehttp.appendToHeaders(headers);
    NotificationClient.ehttp.appendToHeaders(headers);
    CommerceClient.ehttp.appendToHeaders(headers);
    ManagerClient.ehttp.appendToHeaders(headers);
    FlyingMailClient.ehttp.appendToHeaders(headers);
    AutoCloudDnsClient.ehttp.appendToHeaders(headers);
  }
}
